<script setup lang="ts">
import type { SectionCategory } from "@/domains/v4b/site-section/site-section.types";

import { cn } from "@voltalis/frontend-utils";
import { TimesIcon } from "@voltalis/ui-kit/icons";

import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { useTypedIntl } from "@/locales/typed-intl";

import InputText from "primevue/inputtext";

import HospitalityButton from "@/components/ui/buttons/hospitality-button.vue";

type DisplayedSectionCategory = SectionCategory & {
  deleteDisabledTooltip?: string;
};

const props = defineProps<{
  category: DisplayedSectionCategory;
  isEditing: boolean;
  isUpdatingCategory?: boolean;
  isDeletingCategory?: boolean;
}>();

const editedCategoryName = defineModel<string>("editedCategoryName", { required: true });

defineEmits<{
  assignCategory: [category: SectionCategory];
  startEdition: [category: SectionCategory];
  resetEdition: [];
  confirmEdition: [category: SectionCategory];
  openDeleteConfirmation: [category: SectionCategory];
}>();

const { t, k } = useTypedIntl();
</script>

<template>
  <div
    :class="
      cn('rounded-xl transition-colors', {
        'flex flex-col items-start justify-center gap-2 px-2': props.isEditing,
        'flex items-center gap-2 p-2 hover:bg-[#f2f7fe]': !props.isEditing,
      })
    "
    :data-test-id="DATA_TEST_IDS.components.siteSectionCategoryDrawer.categoryRow(props.category.id)"
  >
    <template v-if="props.isEditing">
      <div class="flex w-full items-center gap-2">
        <i class="pi pi-tag shrink-0 text-lg text-text-secondary" />

        <InputText
          v-model="editedCategoryName"
          class="h-12 min-w-0 flex-1 !rounded-lg !border-brand !px-3 !text-base"
          autofocus
          :data-test-id="DATA_TEST_IDS.components.siteSectionCategoryDrawer.editInput(props.category.id)"
          @keydown.enter="$emit('confirmEdition', props.category)"
          @keydown.esc="$emit('resetEdition')"
        />

        <HospitalityButton
          :disabled="props.isUpdatingCategory"
          :data-test-id="DATA_TEST_IDS.components.siteSectionCategoryDrawer.editConfirmButton(props.category.id)"
          is-icon-only
          size="xs"
          @click="$emit('confirmEdition', props.category)"
        >
          <i class="pi pi-check" />
        </HospitalityButton>
      </div>

      <HospitalityButton
        class="!text-danger"
        :disabled="props.isUpdatingCategory"
        :data-test-id="DATA_TEST_IDS.components.siteSectionCategoryDrawer.editCancelButton(props.category.id)"
        :icon="TimesIcon"
        severity="link"
        size="xs"
        @click="$emit('resetEdition')"
      >
        {{ t(k.tools.siteManagement.siteTreeStructure.cancel) }}
      </HospitalityButton>
    </template>

    <template v-else>
      <i class="pi pi-tag shrink-0 text-lg text-text-secondary" />

      <div class="flex min-w-0 flex-1 items-center gap-3">
        <span
          class="min-w-0 truncate text-base font-semibold text-text-primary"
          :data-test-id="DATA_TEST_IDS.components.siteSectionCategoryDrawer.categoryName(props.category.id)"
        >
          {{ props.category.name }}
        </span>

        <span class="text-base text-text-secondary">
          {{ props.category.linkedSectionCount }}
        </span>
      </div>

      <HospitalityButton
        class="!rounded-full hover:!border-brand hover:!bg-brand hover:!text-white"
        :data-test-id="DATA_TEST_IDS.components.siteSectionCategoryDrawer.assignButton(props.category.id)"
        severity="secondary"
        size="xs"
        @click="$emit('assignCategory', props.category)"
      >
        {{ t(k.tools.siteManagement.siteTreeStructure.categoryDrawer.assign) }}
      </HospitalityButton>

      <HospitalityButton
        class="w-[34px] hover:!text-brand"
        :disabled="props.isUpdatingCategory"
        :data-test-id="DATA_TEST_IDS.components.siteSectionCategoryDrawer.editButton(props.category.id)"
        severity="tertiary"
        size="xs"
        @click="$emit('startEdition', props.category)"
      >
        <i class="pi pi-pencil" />
      </HospitalityButton>

      <span
        v-tooltip.top="props.category.deleteDisabledTooltip"
        class="shrink-0"
        :aria-label="props.category.deleteDisabledTooltip"
      >
        <HospitalityButton
          :class="
            cn('w-[34px]', {
              'hover:!bg-danger-soft hover:!text-danger': props.category.isDeletable,
            })
          "
          :disabled="!props.category.isDeletable || props.isDeletingCategory"
          :data-test-id="DATA_TEST_IDS.components.siteSectionCategoryDrawer.deleteButton(props.category.id)"
          severity="tertiary"
          size="xs"
          @click="$emit('openDeleteConfirmation', props.category)"
        >
          <i class="pi pi-trash" />
        </HospitalityButton>
      </span>
    </template>
  </div>
</template>
